import {
  merge,
  omitBy,
  isUndefined,
  isNil,
  forEach,
  isArray,
  mapKeys,
} from 'lodash';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Dispatch } from 'redux';
import { notification } from 'antd';
import { TFunction } from 'i18next';
import { trim } from 'lodash';

import { apiClient } from '@/api/axios';
import { ApiErrorDetail, ApiResponse } from '@/api/types';
import { devError, devLog, logError } from '@/utils/logging';
import { addNotification } from '@/redux/slices/notificationSlice';

export * from '@/api/axios';
export * from '@/api/journeys';
export * from '@/api/ptaReports';
export * from '@/api/types';
export * from '@/api/users';
export * from '@/api/workItems';

export { apiClient };

export const fetchVersionFile = async (): Promise<string> => {
  const response = await axios.get('version.txt', {
    baseURL: window.location.origin,
    timeout: 60 * 1000,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    },
  });
  return trim(response.data);
};

export const callMsGraph = async (
  t: TFunction,
  token?: string
): Promise<ApiResponse<object>> => {
  try {
    const response = await axios.get('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Authorization: token || '',
      },
    });
    return {
      status: response.status,
      data: response.data,
    };
  } catch (error) {
    devError('Error in callMsGraph:', error);
    return handleApiError(t, error as AxiosError);
  }
};

export const formatErrorMessages = (
  errors?: Record<string, string[]>
): string => {
  if (!errors) {
    return 'No details provided.';
  }
  return Object.entries(errors)
    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
    .join('\n');
};

/**
 * What the server said about a failed request, for a dialog or a Notification
 * Center entry: the validation messages, else the detail, else the title.
 */
export const describeProblem = (
  problem: ApiErrorDetail | undefined
): string | undefined => {
  if (problem?.errors) {
    return formatErrorMessages(problem.errors);
  }
  return problem?.detail || problem?.title || undefined;
};

export const handleApiError = (
  t: TFunction,
  error: AxiosError,
  showNotification = true
): ApiResponse<ApiErrorDetail> => {
  if (error.response) {
    const errorData = error.response.data as ApiErrorDetail;
    const { status } = error.response;

    // Skip notification if already on forbidden page
    if (status === 403 && window.location.pathname === '/forbidden') {
      return {
        status,
        data: errorData,
      };
    }

    // Show notification if enabled
    if (showNotification) {
      const message =
        errorData.detail ||
        errorData.title ||
        error.message ||
        'Request failed';

      const description =
        status === 403
          ? t('errors.forbidden')
          : errorData.errors
            ? formatErrorMessages(errorData.errors)
            : errorData.detail || error.message || 'No details provided.';

      notification.error({
        message,
        description,
        duration: 5,
      });
    }

    return {
      status,
      data: errorData,
    };
  }

  return {
    status: 500,
    data: {
      type: 'https://tools.ietf.org/html/rfc7231#section-6.6.1',
      title: 'An error occurred',
      status: 500,
      detail: error.message,
    },
  };
};

interface ApiCallOptions<TData = unknown, TPayload = unknown> {
  /** Redux dispatch function */
  dispatch: Dispatch;
  /** Translation function */
  t: TFunction;
  /** URL path */
  path: string;
  /** HTTP method */
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  /** Request body payload */
  payload?: TPayload;
  /** Custom headers */
  headers?: Record<string, string | undefined>;
  /** Loading action to dispatch before request */
  loadingAction?: (loading: any) => any;
  loadingValue?: boolean | number | string;
  /** Success action to dispatch with response data */
  onSuccess?: (data: TData) => void;
  /** Error action to dispatch with error message */
  onError?: (error: ApiResponse<ApiErrorDetail>) => any;
  /** Success notification config */
  successNotification?: {
    message: string;
    description?: string;
  };
  /** Error notification config */
  errorNotification?: {
    message: string;
    descriptionKey?: string;
  };
  /** Whether to show loading state (default: true) */
  showLoading?: boolean;
  /** Whether to show error notification (default: true) */
  showErrorNotification?: boolean;
}

export const apiCall = async <TData = unknown, TPayload = unknown>(
  options: ApiCallOptions<TData, TPayload>
): Promise<ApiResponse<TData | ApiErrorDetail>> => {
  const {
    dispatch,
    t,
    path,
    method,
    payload,
    headers: customHeaders,
    loadingAction,
    loadingValue = true,
    onSuccess,
    onError,
    successNotification,
    errorNotification,
    showLoading = true,
    showErrorNotification = true,
  } = options;

  const url = path;

  // Dispatch loading state
  if (dispatch && loadingAction && showLoading) {
    dispatch(loadingAction(loadingValue));
  }

  try {
    // Build headers from custom headers
    const headers = buildApiHeaders(customHeaders || {});

    devLog('API Request:', { method, url, headers });

    // Make API call
    const config: AxiosRequestConfig = { headers };
    let response;

    switch (method) {
      case 'post':
        response = await apiClient.post<TData>(url, payload, config);
        break;
      case 'put':
        response = await apiClient.put<TData>(url, payload, config);
        break;
      case 'delete':
        response = await apiClient.delete<TData>(url, config);
        break;
      case 'patch':
        response = await apiClient.patch<TData>(url, payload, config);
        break;
      case 'get':
      default:
        response = await apiClient.get<TData>(url, config);
        break;
    }

    // Handle success
    if (response.status >= 200 && response.status < 300) {
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Show success notification
      if (dispatch && successNotification) {
        dispatch(
          addNotification({
            type: 'success',
            message: successNotification.message,
            description: successNotification.description,
          })
        );
      }

      return {
        status: response.status,
        data: response.data,
        headers: response.headers,
      };
    } else {
      throw response.data as ApiErrorDetail;
    }
  } catch (error: unknown) {
    logError(`Error calling ${method.toUpperCase()} ${url}:`, error);

    const apiError = handleApiError(
      t,
      error as AxiosError,
      showErrorNotification
    );

    if (onError) {
      onError(apiError);
    }

    // Show error notification
    if (dispatch && showErrorNotification && errorNotification) {
      dispatch(
        addNotification({
          type: 'error',
          message: errorNotification.message,
          description: apiError.data.detail,
        })
      );
    }

    return apiError;
  } finally {
    // Clear loading state
    if (dispatch && loadingAction && showLoading) {
      dispatch(loadingAction(typeof loadingValue === 'boolean' ? false : null));
    }
  }
};

const customHeaders = {
  // Response Headers
  apiScheduleVersion: 'x-api-schedule-version', // Schedule version number returned by the backend
  apiScheduleVersionSync: 'x-schedule-version-sync', // Are schedule versions on frontend and backend in sync
  // Request Headers
  appScheduleVersion: 'x-app-schedule-version', // Schedule version number sent by the client
};

/**
 * Merges multiple header objects into a single headers object,
 * removing any keys with undefined values.
 *
 * @param {...Record<string, string | undefined>[]} headerObjects - One or more header objects to merge.
 * @returns {Record<string, string>} - A single headers object with all defined header values.
 */
export const buildApiHeaders = (
  ...headerObjects: Record<string, string | undefined>[]
): Record<string, string> => {
  const mergedHeaders = merge({}, ...headerObjects);
  return omitBy(mergedHeaders, isUndefined) as Record<string, string>;
};

/**
 * Converts all header keys to lowercase for case-insensitive access.
 * @param headers Headers object
 * @returns New object with all keys lowercased
 */
export const normalizeHeaders = (
  headers: Record<string, any>
): Record<string, any> => mapKeys(headers, (_value, key) => key.toLowerCase());

export const appScheduleVersionHeader = (
  version?: string | null | undefined
) => (version ? { [customHeaders.appScheduleVersion]: version } : {});

/**
 * Extracts schedule version headers from an Axios response.
 * @param response Axios response object
 * @returns An object with schedule version headers (if present)
 */
export const getApiScheduleVersionHeaders = (response: {
  headers?: Record<string, string>;
}) => {
  if (!response.headers) {
    return {};
  }

  const normalizedHeaders = normalizeHeaders(response.headers);

  return {
    apiScheduleVersion: normalizedHeaders[customHeaders.apiScheduleVersion],
    apiScheduleVersionSync:
      normalizedHeaders[customHeaders.apiScheduleVersionSync],
  };
};

/**
 * Builds a URL path with query parameters from an object.
 * Automatically handles arrays, undefined/null values, and proper encoding.
 *
 * @param basePath - The base URL path (e.g., '/WorkItems')
 * @param params - Object containing query parameters
 * @returns Complete path with query string
 */
export const buildPathWithParams = (
  basePath: string,
  params?: Record<
    string,
    string | number | boolean | string[] | number[] | undefined | null
  >
): string => {
  if (!params) {
    return basePath;
  }

  // Remove undefined and null values
  const cleanParams = omitBy(params, isNil);

  if (Object.keys(cleanParams).length === 0) {
    return basePath;
  }

  const searchParams = new URLSearchParams();

  forEach(cleanParams, (value, key) => {
    if (isArray(value)) {
      forEach(value, (item) => {
        if (!isNil(item)) {
          searchParams.append(key, String(item));
        }
      });
    } else {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};
